import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  School as TrainingIcon,
  Handshake as MeetingIcon,
  Group as GroupIcon,
  BeachAccess as BeachIcon,
  MailOutline as MailOutlineIcon,
} from '@mui/icons-material';
import { differenceInDays } from 'date-fns';
import api from '../config/api';
import LocationAutocomplete from './LocationAutocomplete';
import { SingleImageUploader } from './ImageUploader';
import {
  DOCUMENT,
  documentSelectMenuProps,
  documentSelectSx,
  documentToggleBtnSx,
} from '../constants/documentTheme';
import { partnerOutlineBtnSx, partnerOxbloodBtnSx } from './PartnerListChrome';
import { getTripLocationsArray } from '../utils/tripUtils';
import { CURRENCIES, getCurrencySymbol } from '../utils/currencyUtils';
import NumericTextField from './NumericTextField';

const tripModalFieldSx = {
  ...documentSelectSx,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    bgcolor: DOCUMENT.surface,
    fontFamily: DOCUMENT.sans,
    color: DOCUMENT.ink,
    '& fieldset': { borderColor: DOCUMENT.ruleHard },
    '&:hover fieldset': { borderColor: DOCUMENT.accent },
    '&.Mui-focused fieldset': { borderColor: DOCUMENT.oxblood, borderWidth: 1 },
  },
  '& .MuiInputLabel-root': {
    fontFamily: DOCUMENT.sans,
    color: DOCUMENT.ink3,
    '&.Mui-focused': { color: DOCUMENT.oxblood },
  },
};

const tripModalSectionTitleSx = {
  mb: 2,
  fontWeight: 500,
  fontFamily: DOCUMENT.sans,
  fontSize: 12,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: DOCUMENT.ink3,
};

// Trip types — translated in component; selection uses document toggle styling
const getTripTypes = (t) => [
  { value: 'business', label: t('trip.modal.types.business'), icon: <BusinessIcon /> },
  { value: 'conference', label: t('trip.modal.types.conference'), icon: <GroupIcon /> },
  { value: 'training', label: t('trip.modal.types.training'), icon: <TrainingIcon /> },
  { value: 'meeting', label: t('trip.modal.types.meeting'), icon: <MeetingIcon /> },
  { value: 'vacation', label: t('trip.modal.types.vacation'), icon: <BeachIcon /> },
];

const TripModal = ({ 
  open, 
  onClose, 
  mode = 'create', // 'create' or 'edit'
  tripId = null,
  initialData = null,
  onSuccess 
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    locations: [{ name: '', coordinates: null }],
    startDate: '',
    endDate: '',
    tripType: 'business',
    budget: '',
    budget_currency: 'USD',
    image_url: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const loadTripData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/trips/${tripId}`);
      const trip = response.data;
      
      // Handle both old single location and new multiple locations format
      let locations = [{ name: '', coordinates: null }];
      locations = getTripLocationsArray(trip);

      setFormData({
        title: trip.title,
        description: trip.description || '',
        locations: locations,
        startDate: trip.start_date,
        endDate: trip.end_date,
        tripType: trip.trip_type,
        budget: trip.budget || '',
        budget_currency: trip.budget_currency || 'USD',
        image_url: trip.image_url || '',
      });
    } catch (err) {
      setError('Failed to load trip details');
      console.error('Error fetching trip:', err);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  // Load trip data for edit mode
  useEffect(() => {
    if (mode === 'edit' && tripId && open) {
      loadTripData();
    } else if (mode === 'create' && open) {
      // Reset form for create mode
      setFormData({
        title: '',
        description: '',
        locations: [{ name: '', coordinates: null }],
        startDate: '',
        endDate: '',
        tripType: 'business',
        budget: '',
        budget_currency: 'USD',
        image_url: '',
      });
      setError(null);
      setSuccess('');
      setValidationErrors({});
    }
  }, [mode, tripId, open, loadTripData]);

  // Location management functions
  const handleLocationChange = useCallback((index, newValue) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.map((loc, i) => 
        i === index ? { ...loc, name: newValue } : loc
      )
    }));
  }, []);

  const handleLocationSelect = useCallback((index, placeData) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.map((loc, i) => 
        i === index ? { 
          name: placeData.address, 
          coordinates: placeData.coordinates 
        } : loc
      )
    }));
  }, []);

  const addLocation = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      locations: [...prev.locations, { name: '', coordinates: null }]
    }));
  }, []);

  const removeLocation = useCallback((index) => {
    if (formData.locations.length > 1) {
      setFormData(prev => ({
        ...prev,
        locations: prev.locations.filter((_, i) => i !== index)
      }));
    }
  }, [formData.locations.length]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [validationErrors]);

  const validateForm = useCallback(() => {
    const errors = {};
    
    if (!formData.title.trim()) errors.title = 'Trip title is required';
    if (formData.title.length > 100) errors.title = 'Title must be less than 100 characters';
    
    // Validate locations
    const validLocations = formData.locations.filter(loc => loc.name && loc.name.trim());
    if (validLocations.length === 0) {
      errors.locations = 'At least one destination is required';
    }
    
    if (!formData.startDate) errors.startDate = 'Start date is required';
    if (!formData.endDate) errors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      errors.endDate = 'End date must be on or after start date';
    }
    if (!formData.tripType) errors.tripType = 'Please select a trip type';
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess('');

    try {
      // Filter out empty locations
      const validLocations = formData.locations.filter(loc => loc.name && loc.name.trim());
      
      const tripData = {
        title: formData.title,
        description: formData.description,
        locations: validLocations,
        start_date: formData.startDate,
        end_date: formData.endDate,
        trip_type: formData.tripType,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        budget_currency: formData.budget_currency
      };
      if (formData.image_url && formData.image_url.trim()) {
        tripData.image_url = formData.image_url.trim();
      }
      if (mode === 'edit') {
        tripData.image_url = formData.image_url ? formData.image_url.trim() : '';
      }

      let response;
      if (mode === 'create') {
        response = await api.post('/trips', tripData);
      } else {
        response = await api.put(`/trips/${tripId}`, tripData);
      }

      if (response.data) {
        setSuccess(mode === 'create' ? 'Trip created successfully!' : 'Trip updated successfully!');
        setTimeout(() => {
          onSuccess?.(response.data);
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${mode} trip`);
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} trip:`, err);
    } finally {
      setSaving(false);
    }
  }, [mode, tripId, formData, validateForm, onSuccess, onClose]);

  const getTripDuration = () => {
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      const days = differenceInDays(endDate, startDate);
      return days > 0 ? `${days} day${days > 1 ? 's' : ''}` : '';
    }
    return '';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen
      PaperProps={{
        sx: {
          borderRadius: 0,
          bgcolor: DOCUMENT.surface,
          color: DOCUMENT.ink,
          fontFamily: DOCUMENT.sans,
          boxShadow: 'none',
          border: { xs: 0, sm: `1px solid ${DOCUMENT.rule}` },
          maxHeight: { xs: '100vh', sm: '90vh' },
          width: { xs: '100vw', sm: '600px' },
          maxWidth: { xs: '100vw', sm: '600px' },
          height: { xs: '100vh', sm: 'auto' },
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: DOCUMENT.nav,
          color: DOCUMENT.ivory,
          py: 2.5,
          px: 3,
          borderBottom: `1px solid ${DOCUMENT.navRule}`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography
              sx={{
                fontFamily: DOCUMENT.display,
                fontWeight: 400,
                fontSize: { xs: '1.35rem', sm: '1.65rem' },
                color: DOCUMENT.ivory,
                lineHeight: 1.2,
              }}
            >
              {mode === 'create' ? t('trip.modal.createTitle') : t('trip.modal.editTitle')}
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                fontFamily: DOCUMENT.sans,
                fontSize: 13,
                color: DOCUMENT.navInk,
              }}
            >
              {mode === 'create' ? t('trip.modal.createSubtitle') : t('trip.modal.editSubtitle')}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label={t('trip.modal.cancel')}
            sx={{
              color: DOCUMENT.ivory,
              borderRadius: 0,
              '&:hover': { bgcolor: 'rgba(246,239,228,0.08)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DialogContent sx={{
          p: { xs: 2, sm: 3 },
          overflow: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
          paddingBottom: '80px',
          bgcolor: DOCUMENT.surface,
        }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 0, border: `1px solid ${DOCUMENT.rule}` }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 0, border: `1px solid ${DOCUMENT.rule}` }}>
              {success}
            </Alert>
          )}

          {!loading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {mode === 'create' && (
                <Alert
                  severity="info"
                  icon={<MailOutlineIcon fontSize="inherit" />}
                  sx={{
                    alignItems: 'flex-start',
                    overflow: 'visible',
                    borderRadius: 0,
                    border: `1px solid ${DOCUMENT.rule}`,
                    bgcolor: DOCUMENT.ivory,
                    color: DOCUMENT.ink2,
                    '& .MuiAlert-icon': { color: DOCUMENT.oxblood },
                    '& .MuiAlert-message': {
                      overflow: 'visible',
                      whiteSpace: 'normal',
                      fontFamily: DOCUMENT.sans,
                    },
                  }}
                >
                  {t('trip.modal.emailReservationsNote')}
                </Alert>
              )}
              <TextField
                label={t('trip.modal.tripTitle')}
                name="title"
                value={formData.title}
                onChange={handleChange}
                error={!!validationErrors.title}
                helperText={validationErrors.title}
                required
                fullWidth
                sx={tripModalFieldSx}
              />

              <TextField
                label={t('trip.modal.descriptionOptional')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                fullWidth
                sx={tripModalFieldSx}
              />

              <Box>
                <Typography sx={{ ...tripModalSectionTitleSx, mb: 1 }}>
                  {t('trip.modal.tripCardImage', 'Trip card image (optional)')}
                </Typography>
                <SingleImageUploader
                  value={formData.image_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, image_url: url || '' }))}
                  preset="cover"
                  folder="trips/covers"
                  label={t('trip.modal.tripCardImageLabel', 'Upload or paste URL')}
                  height={160}
                  cropShape="rect"
                />
              </Box>

              <Box>
                <Typography sx={tripModalSectionTitleSx}>
                  {t('trip.modal.destinations')}
                </Typography>
                {formData.locations.map((location, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <LocationAutocomplete
                      label={t('trip.modal.destination', { number: index + 1 })}
                      placeholder={t('trip.modal.searchLocation')}
                      value={location.name}
                      onChange={(newValue) => handleLocationChange(index, newValue)}
                      onPlaceSelect={(placeData) => handleLocationSelect(index, placeData)}
                      fullWidth
                      sx={tripModalFieldSx}
                    />
                    {formData.locations.length > 1 && (
                      <IconButton
                        onClick={() => removeLocation(index)}
                        sx={{
                          flexShrink: 0,
                          borderRadius: 0,
                          color: DOCUMENT.oxblood,
                          border: `1px solid ${DOCUMENT.rule}`,
                          '&:hover': { bgcolor: DOCUMENT.ivory },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}
                {validationErrors.locations && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {validationErrors.locations}
                  </Typography>
                )}
                <Button
                  startIcon={<AddIcon />}
                  onClick={addLocation}
                  sx={{
                    ...partnerOutlineBtnSx,
                    borderStyle: 'dashed',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {t('trip.modal.addDestination')}
                </Button>
              </Box>

              <Box>
                <Typography sx={tripModalSectionTitleSx}>
                  {t('trip.modal.tripDates')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('trip.modal.startDate')}
                      name="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={handleChange}
                      required
                      error={!!validationErrors.startDate}
                      helperText={validationErrors.startDate}
                      InputLabelProps={{ shrink: true }}
                      sx={tripModalFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('trip.modal.endDate')}
                      name="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={handleChange}
                      required
                      error={!!validationErrors.endDate}
                      helperText={validationErrors.endDate}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: formData.startDate
                      }}
                      sx={tripModalFieldSx}
                    />
                  </Grid>
                </Grid>
                {getTripDuration() && (
                  <Typography sx={{ mt: 1, fontSize: 13, color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
                    {t('trip.modal.duration', { duration: getTripDuration() })}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography sx={tripModalSectionTitleSx}>
                  {t('trip.modal.tripType')}
                </Typography>
                <Grid container spacing={1.5}>
                  {getTripTypes(t).map((type) => {
                    const selected = formData.tripType === type.value;
                    return (
                      <Grid item xs={6} sm={4} key={type.value}>
                        <Button
                          fullWidth
                          onClick={() => setFormData(prev => ({ ...prev, tripType: type.value }))}
                          startIcon={type.icon}
                          sx={{
                            ...documentToggleBtnSx(selected),
                            py: 1.5,
                            justifyContent: 'flex-start',
                          }}
                        >
                          {type.label}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              <Box>
                <Typography sx={tripModalSectionTitleSx}>
                  {t('trip.modal.budget')}
                </Typography>
                <Box>
                  <Typography sx={{ mb: 1, fontSize: 13, fontWeight: 500, color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
                    {t('trip.modal.totalBudget')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <NumericTextField
                        fullWidth
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        allowDecimal
                        allowEmpty
                        placeholder={t('trip.modal.enterAmount')}
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1, color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
                              {getCurrencySymbol(formData.budget_currency)}
                            </Typography>
                          ),
                        }}
                        sx={tripModalFieldSx}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth sx={documentSelectSx}>
                        <Select
                          name="budget_currency"
                          value={formData.budget_currency}
                          onChange={handleChange}
                          MenuProps={documentSelectMenuProps}
                          sx={{ borderRadius: 0 }}
                        >
                          {CURRENCIES.map((currency) => (
                            <MenuItem key={currency.code} value={currency.code}>
                              {currency.symbol} {currency.code}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Typography sx={{ mt: 1, display: 'block', fontSize: 12, color: DOCUMENT.ink3, fontFamily: DOCUMENT.sans }}>
                    {t('trip.modal.budgetHelper')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{
          p: 2,
          gap: 1.5,
          flexDirection: 'row',
          justifyContent: 'flex-end',
          bgcolor: DOCUMENT.canvas,
          borderTop: `1px solid ${DOCUMENT.rule}`,
          flexShrink: 0,
          minHeight: 64,
          position: 'sticky',
          bottom: 0,
          zIndex: 1000,
        }}>
          <Button
            onClick={onClose}
            fullWidth
            sx={{
              ...partnerOutlineBtnSx,
              order: { xs: 2, sm: 1 },
            }}
          >
            {t('trip.modal.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={saving}
            fullWidth
            sx={{
              ...partnerOxbloodBtnSx,
              order: { xs: 1, sm: 2 },
            }}
          >
            {saving ? t('trip.modal.saving') : (mode === 'create' ? t('trip.modal.createButton') : t('trip.modal.updateButton'))}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TripModal;
